import React, { useCallback, useEffect, useRef, useState } from "react";
import { compressImage } from "../lib/imageCompress";

/**
 * Rear camera with an A4 frame. The shutter crops to that frame so the
 * photo sent for OCR is the document, not the desk around it.
 */
export default function DocumentCapture({ busy, error, onCapture, onCancel }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [localError, setLocalError] = useState("");

  const stop = useCallback(() => {
    streamRef.current?.getTracks()?.forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.muted = true;
        await video.play();
        const paint = () => {
          if (!streamRef.current) return;
          const preview = canvasRef.current;
          if (preview && video.videoWidth) {
            if (preview.width !== video.videoWidth) {
              preview.width = video.videoWidth;
              preview.height = video.videoHeight;
            }
            preview.getContext("2d").drawImage(video, 0, 0);
          }
          requestAnimationFrame(paint);
        };
        requestAnimationFrame(paint);
        setReady(true);
      } catch (e) {
        if (!cancelled) setLocalError(e?.message || "Camera unavailable");
      }
    })();
    return () => {
      cancelled = true;
      stop();
    };
  }, [stop]);

  const shoot = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const frame = frameRef.current;
    if (!video?.videoWidth || !canvas || !frame) return;
    const canvasBox = canvas.getBoundingClientRect();
    const frameBox = frame.getBoundingClientRect();
    const scaleX = canvas.width / canvasBox.width;
    const scaleY = canvas.height / canvasBox.height;
    const x = Math.max(0, (frameBox.left - canvasBox.left) * scaleX);
    const y = Math.max(0, (frameBox.top - canvasBox.top) * scaleY);
    const w = Math.min(canvas.width - x, frameBox.width * scaleX);
    const h = Math.min(canvas.height - y, frameBox.height * scaleY);

    const crop = document.createElement("canvas");
    crop.width = Math.max(1, Math.round(w));
    crop.height = Math.max(1, Math.round(h));
    crop.getContext("2d").drawImage(canvas, x, y, w, h, 0, 0, crop.width, crop.height);
    const blob = await new Promise((resolve) => crop.toBlob(resolve, "image/jpeg", 0.92));
    if (!blob) return;
    const file = new File([blob], "document-scan.jpg", { type: "image/jpeg" });
    const compressed = await compressImage(file, { maxEdge: 2000, quality: 0.85 });
    onCapture(compressed);
  };

  return (
    <div className="mt-6" data-testid="document-capture">
      <p className="label-tag mb-2">Fit the whole page inside the frame</p>
      {(error || localError) && <p className="mb-2 label-tag text-red-600">{error || localError}</p>}
      <div className="relative border-2 border-black bg-black overflow-hidden">
        <video ref={videoRef} className="hidden" playsInline muted />
        <canvas ref={canvasRef} className="block w-full h-auto" />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div
            ref={frameRef}
            className="relative border-2 border-[color:var(--cc-yellow)]"
            style={{ height: "86%", maxWidth: "90%", aspectRatio: `210 / 297` }}
          >
            {["tl", "tr", "bl", "br"].map((corner) => (
              <span
                key={corner}
                className={`absolute h-5 w-5 border-[color:var(--cc-yellow)] ${
                  corner.includes("t") ? "top-0 border-t-4" : "bottom-0 border-b-4"
                } ${corner.includes("l") ? "left-0 border-l-4" : "right-0 border-r-4"}`}
              />
            ))}
          </div>
        </div>
        {!ready && !localError && (
          <div className="absolute inset-0 flex items-center justify-center text-white label-tag">
            Starting camera…
          </div>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={!ready || busy}
          onClick={shoot}
          className="flex-1 label-tag px-4 py-3 bg-black text-white brutal-btn disabled:opacity-60"
        >
          {busy ? "Checking…" : "Capture page"}
        </button>
        <button type="button" onClick={onCancel} className="label-tag px-3 py-3 underline">
          Cancel
        </button>
      </div>
    </div>
  );
}
